import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import api from '../../services/api';

const DigestEmailPage = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [sendToAll, setSendToAll] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [reminderMessage, setReminderMessage] = useState({ type: '', text: '' });
  const [results, setResults] = useState(null);
  const [reminderResults, setReminderResults] = useState(null);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await api.get('/email/employees');
      setEmployees(response.data.data);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to fetch employees'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendDigest = async () => {
    if (!sendToAll && !selectedEmployee) {
      setMessage({ type: 'error', text: 'Please select an employee or choose "Send to All"' });
      return;
    }

    try {
      setSending(true);
      setMessage({ type: '', text: '' });
      setResults(null);

      const body = sendToAll ? {} : { employeeId: selectedEmployee };
      const response = await api.post('/email/test-digest', body);

      setMessage({ type: 'success', text: response.data.message });
      setResults(response.data.data);

      // Reset form
      setSelectedEmployee('');
      setSendToAll(false);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to send digest email'
      });
    } finally {
      setSending(false);
    }
  };

  const handleSendReminderTest = async () => {
    try {
      setSendingReminders(true);
      setReminderMessage({ type: '', text: '' });
      setReminderResults(null);

      const response = await api.post('/attendance-reminders/test-reminders');

      setReminderMessage({
        type: 'success',
        text: `Test reminder emails sent successfully to ${response.data.data.adminCount} admin(s)`
      });
      setReminderResults(response.data.data);
    } catch (error) {
      setReminderMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to send test reminder emails'
      });
    } finally {
      setSendingReminders(false);
    }
  };

  return (
    <Layout>
      <div className="container-fluid" style={{ maxWidth: '900px' }}>
        <div className="mb-4">
          <h1 className="fs-3 mb-1">Email Testing</h1>
          <p className="mb-0 text-muted">Test and send various system emails</p>
        </div>

        {/* Weekly Digest Section */}
        <div className="card mb-3">
          <div className="card-body">
            <h2 className="fs-5 mb-3">Weekly Digest Email</h2>

            <div className="form-check border rounded-2 p-3 mb-3">
              <input
                type="checkbox"
                className="form-check-input"
                id="sendToAllCheck"
                checked={sendToAll}
                onChange={(e) => {
                  setSendToAll(e.target.checked);
                  if (e.target.checked) {
                    setSelectedEmployee('');
                  }
                }}
              />
              <label className="form-check-label" htmlFor="sendToAllCheck">
                Send to All Active Employees
              </label>
            </div>

            {!sendToAll && (
              <div className="mb-3">
                <label className="form-label">Select Employee</label>
                <select
                  className="form-select"
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  disabled={loading || sendToAll}
                >
                  <option value="">-- Select an Employee --</option>
                  {employees.map((emp) => (
                    <option key={emp._id} value={emp._id}>
                      {emp.name} ({emp.employee_code}) - {emp.email}
                    </option>
                  ))}
                </select>
                {employees.length > 0 && (
                  <small className="form-text text-muted">
                    {employees.length} active employees available
                  </small>
                )}
              </div>
            )}

            <button
              className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2"
              onClick={handleSendDigest}
              disabled={sending || loading || (!sendToAll && !selectedEmployee)}
            >
              {sending ? (
                <>
                  <span className="spinner-border spinner-border-sm" />
                  Sending...
                </>
              ) : (
                <>
                  <i className="ti ti-send" />
                  Send Digest Email
                </>
              )}
            </button>

            {message.text && (
              <div className={`alert mt-3 mb-0 ${message.type === 'success' ? 'alert-success' : 'alert-danger'}`}>
                {message.text}
              </div>
            )}

            {results && (
              <div className="mt-4 pt-4 border-top">
                <h3 className="fs-6 mb-3">Send Results</h3>
                <div className="row g-2 mb-3">
                  <div className="col-4">
                    <div className="border rounded-2 text-center py-3">
                      <div className="fs-4 fw-bold">{results.total || 1}</div>
                      <div className="text-muted small text-uppercase">Total</div>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="border border-success rounded-2 text-center py-3 bg-success bg-opacity-10">
                      <div className="fs-4 fw-bold text-success">{results.successful || (results.success ? 1 : 0)}</div>
                      <div className="text-muted small text-uppercase">Successful</div>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="border border-danger rounded-2 text-center py-3 bg-danger bg-opacity-10">
                      <div className="fs-4 fw-bold text-danger">{results.failed || (results.success ? 0 : 1)}</div>
                      <div className="text-muted small text-uppercase">Failed</div>
                    </div>
                  </div>
                </div>

                {results.results && results.results.length > 0 && (
                  <div>
                    <h4 className="text-muted text-uppercase small fw-semibold mb-2">Details</h4>
                    <div className="bg-light rounded-2 p-2" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {results.results.map((result, index) => (
                        <div
                          key={index}
                          className={`d-flex align-items-center gap-2 p-2 mb-1 bg-white rounded-2 border-start border-3 ${
                            result.success ? 'border-success' : 'border-danger'
                          }`}
                        >
                          <i className={`ti ${result.success ? 'ti-circle-check text-success' : 'ti-circle-x text-danger'}`} />
                          <span className="flex-grow-1 fw-medium small">{result.employee}</span>
                          {result.error && (
                            <span className="text-danger small fst-italic">{result.error}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Attendance Reminders Section */}
        <div className="card">
          <div className="card-body">
            <h2 className="fs-5 mb-3">Attendance Reminders</h2>

            <p className="text-muted mb-3">
              Send test check-in and check-out reminder emails to all admins
            </p>

            <button
              className="btn btn-warning w-100 d-flex align-items-center justify-content-center gap-2"
              onClick={handleSendReminderTest}
              disabled={sendingReminders}
            >
              {sendingReminders ? (
                <>
                  <span className="spinner-border spinner-border-sm" />
                  Sending Test Reminders...
                </>
              ) : (
                <>
                  <i className="ti ti-send" />
                  Send Test Reminder Emails to Admins
                </>
              )}
            </button>

            {reminderMessage.text && (
              <div className={`alert mt-3 mb-0 ${reminderMessage.type === 'success' ? 'alert-success' : 'alert-danger'}`}>
                {reminderMessage.text}
              </div>
            )}

            {reminderResults && (
              <div className="mt-4 pt-4 border-top">
                <h3 className="fs-6 mb-3">Email Results</h3>
                <div className="stats-grid mb-3">
                  <div className="stat-card">
                    <h3>Admins</h3>
                    <p className="stat-value">{reminderResults.adminCount}</p>
                  </div>
                  <div className="stat-card">
                    <h3>Check-in Emails</h3>
                    <p className="stat-value text-success">{reminderResults.checkInEmailsSent}</p>
                  </div>
                  <div className="stat-card">
                    <h3>Check-out Emails</h3>
                    <p className="stat-value text-success">{reminderResults.checkOutEmailsSent}</p>
                  </div>
                  <div className="stat-card">
                    <h3>Total Sent</h3>
                    <p className="stat-value">{reminderResults.totalEmailsSent}</p>
                  </div>
                </div>

                {reminderResults.recipients && reminderResults.recipients.length > 0 && (
                  <div className="bg-light rounded-2 p-3 mb-3">
                    <h4 className="small fw-semibold mb-2">Recipients:</h4>
                    <div className="d-flex gap-2 flex-wrap">
                      {reminderResults.recipients.map((email, index) => (
                        <span key={index} className="badge bg-primary">
                          {email}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {reminderResults.errors && reminderResults.errors.length > 0 && (
                  <div className="bg-danger bg-opacity-10 border-start border-danger border-3 rounded-2 p-3">
                    <h4 className="small fw-semibold text-danger mb-2">Errors:</h4>
                    {reminderResults.errors.map((err, index) => (
                      <div key={index} className="small text-danger py-1">
                        {err.email} ({err.type}): {err.error}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DigestEmailPage;
